import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { syncQueue } from './syncQueue';
import { useOfflineStatus } from '@shared/offline/OfflineProvider';
import { SyncQueueItem } from '@shared/storage/indexedDb';
import { logger } from '@shared/logging/logger';

interface SyncContextType {
  isSyncing: boolean;
  queuedItems: SyncQueueItem[];
  triggerSync: () => Promise<boolean>;
  getPendingCount: () => number;
}

const SyncContext = createContext<SyncContextType | undefined>(undefined);

export const SyncProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isOnline } = useOfflineStatus();
  const [isSyncing, setIsSyncing] = useState(false);
  const isSyncingRef = React.useRef(false);
  const [queuedItems, setQueuedItems] = useState<SyncQueueItem[]>([]);

  // Function to refresh current list of queued items
  const reloadQueue = useCallback(async () => {
    const list = await syncQueue.getQueue();
    setQueuedItems(list);
  }, []);

  // Sync execution logic
  const triggerSync = useCallback(async (): Promise<boolean> => {
    if (!isOnline || isSyncingRef.current) return false;
    
    isSyncingRef.current = true;
    setIsSyncing(true);
    try {
      const result = await syncQueue.processQueue();
      await reloadQueue();
      return result;
    } finally {
      isSyncingRef.current = false;
      setIsSyncing(false);
    }
  }, [isOnline, reloadQueue]);

  // Read queue size when starting up or changing network status
  useEffect(() => {
    reloadQueue();
  }, [reloadQueue, isOnline]);

  // Synchronize when the user transitions from offline back to online
  useEffect(() => {
    if (isOnline) {
      logger.info('sync_provider', 'System is online; executing flush of sync queues.');
      triggerSync();
    }
  }, [isOnline, triggerSync]);

  // Background retry timer loop to processes failed entries
  useEffect(() => {
    const interval = setInterval(() => {
      if (isOnline) {
        triggerSync();
      }
    }, 45000); // Poll/resync queue every 45s

    return () => clearInterval(interval);
  }, [isOnline, triggerSync]);

  const getPendingCount = useCallback(() => queuedItems.length, [queuedItems.length]);

  const contextValue = useMemo(() => ({
    isSyncing,
    queuedItems,
    triggerSync,
    getPendingCount,
  }), [isSyncing, queuedItems, triggerSync, getPendingCount]);

  return (
    <SyncContext.Provider value={contextValue}>
      {children}
    </SyncContext.Provider>
  );
};

export const useSync = () => {
  const context = useContext(SyncContext);
  if (context === undefined) {
    throw new Error('useSync must be used within a SyncProvider');
  }
  return context;
};

export default SyncProvider;
